/**
 * The board itself (screens 14, 15, 16), after the v1 board cell: team or
 * axis labels sit OUTSIDE the grid, the number headers read "?" until the
 * server draws and shows them, an open square shows "+", a held square shows
 * its holder's initials, the viewer's own squares carry a brand outline, and
 * a winning square is filled. The grid fits the width at 320px and never
 * scrolls sideways.
 *
 * The board never decides who may tap what. The parent passes `isCellEnabled`
 * from the server's viewer fields.
 *
 * ISSUE-1270 QA round 3. A 100-square board at 390px leaves each square about
 * 28px, so initials are drawn in a small SVG that scales with the square and
 * never grows past the 12px small type: two letters always fit, from 320px up
 * to the desktop board. A guest's square has a dashed border instead of a
 * visible Guest tag, which has no room at these sizes (screen readers still
 * hear "guest" in the label and the sr-only tag).
 */
import { cn, FOCUS_RING, REDUCED_MOTION_SAFE } from "@sportsmet/ui";
import { type ReactNode, useEffect, useRef } from "react";

import {
  SQUARES_BOARD_LABEL,
  SQUARES_GUEST_TAG,
  squaresSquareAriaLabel,
} from "./squaresCopy";
import {
  squaresCellInitials,
  type typeSquaresBoardCell,
  type typeSquaresBoardLayout,
} from "./squaresModel";

export type typeSquaresBoardProps = {
  layout: typeSquaresBoardLayout;
  topLabel: string;
  sideLabel: string;
  isCellEnabled: (cell: typeSquaresBoardCell) => boolean;
  onCellPress: (cell: typeSquaresBoardCell) => void;
  /** The square the Results tab jumped to, ringed and scrolled into view. */
  focusKey?: string | null | undefined;
  /** The square picked in the Add a winner sheet. */
  pickedKey?: string | null | undefined;
  /** The square whose take or release is in flight. */
  busyKey?: string | null | undefined;
  testId?: string | undefined;
};

function cellClass(input: {
  cell: typeSquaresBoardCell;
  isEnabled: boolean;
  isFocused: boolean;
  isPicked: boolean;
}): string {
  const { cell } = input;
  return cn(
    "relative flex aspect-square w-full min-w-0 items-center justify-center overflow-hidden rounded-sm border text-small",
    "transition-colors duration-200 ease-standard",
    REDUCED_MOTION_SAFE,
    FOCUS_RING,
    cell.isWinning
      ? "border-accent bg-accent-solid font-semibold text-on-accent"
      : cell.square === null
        ? "border-line bg-surface text-ink-tertiary"
        : "border-hairline bg-surface-sunken font-semibold text-ink",
    cell.square?.isGuest === true ? "border-dashed" : undefined,
    cell.square?.isGuest === true && !cell.isWinning
      ? "border-line-strong"
      : undefined,
    cell.isMine && !cell.isWinning ? "border-2 border-primary" : undefined,
    cell.isMine && cell.isWinning ? "ring-2 ring-primary" : undefined,
    input.isPicked ? "ring-2 ring-primary ring-offset-1" : undefined,
    input.isFocused ? "ring-2 ring-warning ring-offset-1" : undefined,
    input.isEnabled
      ? "cursor-pointer hover:border-line-strong active:scale-95"
      : "cursor-default",
  );
}

/**
 * The holder's initials, 12 units tall in a 24 by 24 viewBox capped at 24px:
 * the 12px small type on any square of 24px or more (a 390px phone), and
 * scaled down with a smaller square (about 9px at 320) instead of cut off.
 * `currentColor` keeps the cell's text color in light and dark.
 */
const SquaresCellInitials = (props: { initials: string }): ReactNode => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    className="block size-full max-h-6 max-w-6"
    data-testid="squares-cell-initials"
  >
    <text
      x="12"
      y="12"
      textAnchor="middle"
      dominantBaseline="central"
      fill="currentColor"
      fontSize="12"
      fontWeight="600"
    >
      {props.initials}
    </text>
  </svg>
);

export const SquaresBoard = (props: typeSquaresBoardProps): ReactNode => {
  const { layout } = props;
  const colCount = layout.topHeaders.length;
  const focusRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (props.focusKey === null || props.focusKey === undefined) return;
    const target = focusRef.current;
    if (target === null || typeof target.scrollIntoView !== "function") return;
    target.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [props.focusKey]);

  return (
    <div
      className="flex w-full flex-col gap-1"
      data-testid={props.testId ?? "squares-board"}
    >
      <p
        className="truncate ps-6 text-center text-caption font-semibold text-ink"
        data-testid="squares-board-top-label"
      >
        {props.topLabel}
      </p>
      <div className="flex w-full items-stretch gap-1">
        <p
          className="flex w-4 shrink-0 items-center justify-center text-caption font-semibold text-ink [writing-mode:vertical-rl] rotate-180"
          data-testid="squares-board-side-label"
        >
          <span className="truncate">{props.sideLabel}</span>
        </p>
        <div
          role="group"
          aria-label={SQUARES_BOARD_LABEL}
          className="grid min-w-0 flex-1 gap-1"
          style={{
            gridTemplateColumns: `minmax(0, 0.7fr) repeat(${String(colCount)}, minmax(0, 1fr))`,
          }}
        >
          <span aria-hidden="true" />
          {layout.topHeaders.map((header, index) => (
            <span
              key={`top-${String(index)}`}
              data-testid="squares-top-header"
              className="flex items-center justify-center rounded-sm bg-surface-sunken text-small font-semibold text-ink-secondary"
            >
              {header}
            </span>
          ))}
          {layout.rows.map((row, rowIndex) => (
            <div key={`row-${String(rowIndex)}`} className="contents">
              <span
                data-testid="squares-side-header"
                className="flex items-center justify-center rounded-sm bg-surface-sunken text-small font-semibold text-ink-secondary"
              >
                {layout.sideHeaders[rowIndex]}
              </span>
              {row.map((cell) => {
                const isEnabled = props.isCellEnabled(cell);
                const isFocused = props.focusKey === cell.squareKey;
                const isPicked = props.pickedKey === cell.squareKey;
                const isBusy = props.busyKey === cell.squareKey;
                return (
                  <button
                    key={cell.squareKey}
                    ref={isFocused ? focusRef : undefined}
                    type="button"
                    data-testid={`squares-cell-${cell.squareKey}`}
                    data-mine={cell.isMine ? "true" : undefined}
                    data-winning={cell.isWinning ? "true" : undefined}
                    aria-label={squaresSquareAriaLabel({
                      row: cell.row,
                      col: cell.col,
                      holder: cell.square?.displayName ?? null,
                      isMine: cell.isMine,
                      isWinning: cell.isWinning,
                    })}
                    aria-pressed={isPicked ? true : undefined}
                    aria-busy={isBusy || undefined}
                    disabled={!isEnabled || isBusy}
                    className={cellClass({
                      cell,
                      isEnabled,
                      isFocused,
                      isPicked,
                    })}
                    onClick={() => {
                      props.onCellPress(cell);
                    }}
                  >
                    {cell.square === null ? (
                      isEnabled ? (
                        <span aria-hidden="true">+</span>
                      ) : null
                    ) : (
                      <SquaresCellInitials
                        initials={squaresCellInitials(cell.square)}
                      />
                    )}
                    {cell.square?.isGuest === true ? (
                      <span className="sr-only">{SQUARES_GUEST_TAG}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
